import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, PieChart, CandlestickChart, LineChart, Wallet as WalletIcon, Gem } from "lucide-react";
import { useApp } from "@/context/AppContext";

const MARKET_ROUTES = [
  "/markets", "/trade-global", "/trade-eu", "/trade-asia", "/trade-gcc",
  "/trade-world", "/trade-forex", "/trade-commodities", "/trade-crypto",
];

// Simple mode: the five things a client actually does. Pro mode adds the trading desk.
const SIMPLE_TABS = [
  { to: "/", label: "Home", icon: Home, match: ["/"] },
  { to: "/invest", label: "Invest", icon: PieChart, match: ["/invest"] },
  { to: "/trade-global", label: "Stocks", icon: LineChart, match: ["/trade-global", "/trade-gcc"], prefix: ["/stock/"] },
  { to: "/markets", label: "Markets", icon: Gem, match: MARKET_ROUTES.filter((r) => !["/trade-global", "/trade-gcc"].includes(r)) },
  { to: "/wallet", label: "Wallet", icon: WalletIcon, match: ["/wallet", "/save"] },
];

const PRO_TABS = [
  { to: "/", label: "Home", icon: Home, match: ["/"] },
  { to: "/invest", label: "Invest", icon: PieChart, match: ["/invest", "/insights", "/live"] },
  { to: "/broker", label: "Trade", icon: CandlestickChart, match: ["/broker", "/orders"], prefix: ["/stock/"] },
  { to: "/markets", label: "Markets", icon: LineChart, match: MARKET_ROUTES },
  { to: "/wallet", label: "Wallet", icon: WalletIcon, match: ["/wallet", "/save"] },
];

/** Mobile-only bottom navigation. Desktop keeps the horizontal top nav. */
export const BottomNav = () => {
  const { pathname } = useLocation();
  const { user } = useApp();
  const TABS = user?.pro_mode ? PRO_TABS : SIMPLE_TABS;
  const isActive = (tab) =>
    tab.match.includes(pathname) || (tab.prefix || []).some((p) => pathname.startsWith(p));

  return (
    <nav data-testid="bottom-nav"
      className="lg:hidden fixed bottom-0 inset-x-0 z-50 border-t border-border/60 bg-background/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]">
      <div className="grid grid-cols-5">
        {TABS.map((tab) => {
          const active = isActive(tab);
          return (
            <NavLink key={tab.to} to={tab.to} data-testid={`bottom-nav-${tab.label.toLowerCase()}`}
              className="relative flex flex-col items-center justify-center gap-1 py-2 active:scale-95 transition-transform">
              <span className={`absolute top-0 h-[2px] w-8 rounded-full transition-opacity ${
                active ? "bg-[color:var(--tharwy-lime)] opacity-100" : "opacity-0"}`} />
              <span className={`flex h-8 w-12 items-center justify-center rounded-full transition-colors ${
                active ? "bg-accent" : "bg-transparent"}`}>
                <tab.icon className={`h-[19px] w-[19px] transition-colors ${
                  active ? "text-[hsl(var(--accent-foreground))]" : "text-muted-foreground"}`} />
              </span>
              <span className={`text-[11px] transition-colors ${
                active ? "text-foreground font-semibold" : "text-muted-foreground font-medium"}`}>
                {tab.label}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};
